import { Checkbox } from '@atlaskit/checkbox';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { useDatabaseContent } from '@databases/database-state';
import {
  EditableListWithPlaceholder,
  type EditableListProps,
} from '@databases/editable-list';
import UserPicker from '@databases/field-picker/UserPicker';
import FieldDecorator from '@databases/form/FieldDecorator';
import { messages } from '@databases/intl/messages';
import { useMentionNotification } from '@databases/mention-notification';
import { useCallbackEffect } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { type UserReference } from '@databases/types/confluence';
import {
  DatabaseFieldType,
  type RawDatabaseValue,
  USER_VALUE_CURRENT,
} from '@databases/types/database';
import { MultiValueChangeType } from '@databases/utils/database/changesets';
import { isNonEmptyUserValue } from '@databases/utils/database/field-types/user';
import { selectCurrentUserId } from '@databases/utils/redux/context/selectors';
import { loadUserById } from '@databases/utils/redux/users/actions';
import { selectUserByIdOptional } from '@databases/utils/redux/users/selectors';
import { generateId } from '@databases/utils/utils';
import React, {
  type FunctionComponent,
  useCallback,
  useMemo,
  useEffect,
  useState,
  useRef,
} from 'react';
import { useIntl } from 'react-intl-next';

import { type EditComponentProps } from './types';

type Value = Exclude<RawDatabaseValue<DatabaseFieldType.USER>, string>[0];

const getId = (value: Value): string => value._id;

const getNewValue = (): Value => ({ _id: generateId() });

const UserValueEdit: FunctionComponent<EditComponentProps<DatabaseFieldType.USER>> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  field,
  isDefaultValue,
}) => {
  const { contentId } = useDatabaseContent();
  const { scheduleMentionNotification, flushMentions } = useMentionNotification({
    contentId,
  });
  const analyticsClient = useAnalytics();
  const currentUserId = defaultStoreUtils.useSelector(selectCurrentUserId);
  const currentUser = defaultStoreUtils.useSelector((state) =>
    selectUserByIdOptional(state, { accountId: currentUserId })
  );
  const flushMentionsRef = useRef(flushMentions);
  flushMentionsRef.current = flushMentions;

  useEffect(() => {
    return () => {
      flushMentionsRef.current();
    };
  }, []);

  const defaultId = useMemo(() => generateId(), []);

  const dispatch = defaultStoreUtils.useDispatch();
  useCallbackEffect(
    useCallback(async () => {
      if (value === USER_VALUE_CURRENT && currentUserId) {
        await dispatch(loadUserById(currentUserId));
      }
    }, [currentUserId, dispatch, value])
  );

  const { formatMessage } = useIntl();

  const handleToggleCurrent = useCallback(() => {
    if (value === USER_VALUE_CURRENT) {
      const userValues = currentUser ? [{ ...currentUser, _id: defaultId }] : [];
      onChange(userValues, {
        type: DatabaseFieldType.USER,
        delta: {
          type: MultiValueChangeType.SET_TO,
          value: userValues,
        },
      });
    } else {
      onChange(USER_VALUE_CURRENT, {
        type: DatabaseFieldType.USER,
        delta: {
          type: MultiValueChangeType.SET_TO,
          value: USER_VALUE_CURRENT,
        },
      });
    }
  }, [defaultId, currentUser, onChange, value]);

  const resolvedValue = defaultStoreUtils.useEqualSelector((state): Value[] => {
    let result: Value[];
    if (value === USER_VALUE_CURRENT) {
      result = currentUser ? [{ ...currentUser, _id: defaultId }] : [];
    } else {
      // Use updated display names if a user was renamed
      result = value.map((v) => {
        const user = isNonEmptyUserValue(v)
          ? selectUserByIdOptional(state, { accountId: v.accountId })
          : undefined;
        return user ? { ...v, displayName: user.displayName } : v;
      });
    }
    return result;
  });

  const [inputValue, setInputValue] = useState<Value[]>(resolvedValue);

  const handleSingleChange = useCallback(
    (val: UserReference[]) => {
      const userValues = [];
      if (val.length > 0) {
        userValues.push({ ...val[0], _id: inputValue[0]?._id ?? generateId() });

        analyticsClient.sendUserMentionTypeaheadClickedEvent({
          contentId,
          databaseContainer: getDatabaseContainerType(),
        });
      }

      setInputValue(userValues);

      onChange(userValues, {
        type: DatabaseFieldType.USER,
        delta: {
          type: MultiValueChangeType.SET_TO,
          value: userValues,
        },
      });

      if (
        field.settings.mentionNotificationEnabled &&
        userValues.length > 0 &&
        contentId &&
        userValues[0].displayName
      ) {
        scheduleMentionNotification({
          mentions: [
            {
              mentionLocalId: userValues[0]._id,
              mentionedUserAccountId: userValues[0].accountId,
              displayName: userValues[0].displayName,
            },
          ],
          fieldId: field.id,
        });
      }
    },
    [
      onChange,
      contentId,
      field.settings.mentionNotificationEnabled,
      inputValue,
      scheduleMentionNotification,
      field.id,
      analyticsClient,
    ]
  );

  const onItemChange = useCallback(
    (val: Value, newValueIndex: number, _: null, allValues: Value[]) => {
      setInputValue(allValues);
      onChange(allValues, {
        type: DatabaseFieldType.USER,
        delta: {
          type: MultiValueChangeType.EDIT,
          value: val,
          index: newValueIndex,
        },
      });
    },
    [onChange]
  );

  const onItemAdd = useCallback(
    async (val: Value, allValues: Value[]) => {
      setInputValue(allValues);

      onChange(allValues, {
        type: DatabaseFieldType.USER,
        delta: {
          type: MultiValueChangeType.APPEND,
          value: val,
        },
      });

      analyticsClient.sendUserMentionTypeaheadClickedEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
      });

      const user = val as UserReference;
      if (
        field.settings.mentionNotificationEnabled &&
        allValues.length > 0 &&
        contentId &&
        user.displayName
      ) {
        scheduleMentionNotification({
          mentions: [
            {
              mentionLocalId: val._id,
              mentionedUserAccountId: user.accountId,
              displayName: user.displayName,
            },
          ],
          fieldId: field.id,
        });
      }
    },
    [
      onChange,
      contentId,
      field.settings.mentionNotificationEnabled,
      scheduleMentionNotification,
      field.id,
      analyticsClient,
    ]
  );

  const onItemDelete = useCallback(
    (_: Value, removedIndex: number, allValues: Value[]) => {
      setInputValue(allValues);
      onChange(allValues, {
        type: DatabaseFieldType.USER,
        delta: {
          type: MultiValueChangeType.REMOVE,
          index: removedIndex,
        },
      });
    },
    [onChange]
  );

  const onItemReorder = useCallback(
    (_: Value, currentIndex: number, newIndex: number, allValues: Value[]) => {
      setInputValue(allValues);
      onChange(allValues, {
        type: DatabaseFieldType.USER,
        delta: {
          type: MultiValueChangeType.REORDER,
          currentIndex,
          newIndex,
        },
      });
    },
    [onChange]
  );

  const isMulti =
    (field.settings.multi || inputValue.length > 1) &&
    !(isDefaultValue && value === USER_VALUE_CURRENT);

  const renderField: EditableListProps<Value, null>['renderField'] = useCallback(
    ({ value, autoFocus, onChange }) => (
      <UserPicker
        value={isNonEmptyUserValue(value) ? [value] : []}
        onChange={(val) => {
          onChange({ ...(val[0] ?? {}), _id: value._id }, null);
        }}
        autoFocus={autoFocus}
        isSeamless
        isMentionPreview
      />
    ),
    []
  );

  let picker;
  if (isMulti) {
    picker = (
      <EditableListWithPlaceholder<Value, null>
        getId={getId}
        values={inputValue}
        onItemChange={onItemChange}
        onAddNewItem={onItemAdd}
        onRemoveItem={onItemDelete}
        onReorder={onItemReorder}
        getNewValue={getNewValue}
        renderField={renderField}
        autoFocus={value.length === 0 ? autoFocus : undefined}
        addLabel={formatMessage(messages['field-settings.type-user-add-user-label-text'])}
        noAdd={!field.settings.multi}
        onBlur={onBlur}
        onFocus={onFocus}
      />
    );
  } else {
    const userPickerValue =
      inputValue[0] && isNonEmptyUserValue(inputValue[0]) ? [inputValue[0]] : [];
    picker = (
      <UserPicker
        value={userPickerValue}
        onChange={handleSingleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        autoFocus={autoFocus}
        isClearable
        isDisabled={isDefaultValue && value === USER_VALUE_CURRENT}
        isMentionPreview
      />
    );
  }

  if (isDefaultValue) {
    return (
      <>
        {picker}
        <FieldDecorator isCompact>
          <Checkbox
            isChecked={value === USER_VALUE_CURRENT}
            onChange={handleToggleCurrent}
            onFocus={onFocus}
            onBlur={onBlur}
            label={formatMessage(
              messages['field-settings.type-user-set-current-user-default-text']
            )}
          />
        </FieldDecorator>
      </>
    );
  } else {
    return picker;
  }
};

export default UserValueEdit;
